import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty, reject, omit } from 'ramda';
import { isBlank } from 'utils/helperMethods';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';

import { Button, TextField, Typography } from '@material-ui/core';
import validationSchema, { getDefaultAttributes } from 'forms/realEstateAgentProfileForm';
import UserPresenter from 'presenters/UserPresenter';
import AddressSelect from 'sharedComponents/AddressSelect';

import CompanyLogo from './components/CompanyLogo';
import Avatar from './components/Avatar';
import PhoneInput from './components/PhoneInput';

import useStyles from './useStyles';

const ProfileForm = props => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { onSubmit, currentUser, saveProfileImage, saveCompanyLogo, isProfileImageSaving, isCompanyLogoSaving } = props;

  const formik = useFormik({
    validationSchema,
    initialValues: getDefaultAttributes(currentUser),
    onSubmit: values => onSubmit(omit(['streetAddress'], values)),
  });

  const { errors, values, handleChange, isSubmitting, handleBlur, touched, handleSubmit, setFieldValue } = formik;

  const isAbleToSubmit = isEmpty(errors) && !isSubmitting;

  const companyLogoUrl = UserPresenter.companyLogoUrl(currentUser);
  const imageUrl = UserPresenter.profileImageUrl(currentUser);

  const { city, streetAddress1, streetAddress2, state, zipCode } = values;
  const fullAddress = reject(isBlank, [streetAddress1, streetAddress2, city, state, zipCode]).join(' ');
  const streetAddress = reject(isBlank, [streetAddress1, streetAddress2]).join(' ');

  return (
    <div className={classes.container}>
      <form className={classes.form} onSubmit={handleSubmit}>
        <Avatar
          isSaving={isProfileImageSaving}
          onFinish={saveProfileImage}
          value={imageUrl}
          alt={t('components.RealEstateAgentSettingsPage.ProfileForm.userImage')}
        />
        <TextField
          error={touched.email && Boolean(errors.email)}
          helperText={touched.email && t(errors.email)}
          variant="outlined"
          id="email"
          name="email"
          type="email"
          label={t('components.RealEstateAgentSettingsPage.ProfileForm.email')}
          autoComplete="email"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.email}
          className={classes.field}
          InputLabelProps={{
            shrink: false,
          }}
          InputProps={{
            style: {
              height: '40px',
            },
          }}
        />
        <TextField
          error={touched.firstName && Boolean(errors.firstName)}
          helperText={touched.firstName && t(errors.firstName)}
          variant="outlined"
          id="firstName"
          name="firstName"
          type="text"
          label={t('components.RealEstateAgentSettingsPage.ProfileForm.firstName')}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.firstName}
          className={classes.field}
          InputLabelProps={{
            shrink: false,
          }}
          InputProps={{
            style: {
              height: '40px',
            },
          }}
        />
        <TextField
          error={touched.lastName && Boolean(errors.lastName)}
          helperText={touched.lastName && t(errors.lastName)}
          variant="outlined"
          id="lastName"
          name="lastName"
          type="text"
          label={t('components.RealEstateAgentSettingsPage.ProfileForm.lastName')}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.lastName}
          className={classes.field}
          InputLabelProps={{
            shrink: false,
          }}
          InputProps={{
            style: {
              height: '40px',
            },
          }}
        />
        <CompanyLogo
          label={t('components.RealEstateAgentSettingsPage.ProfileForm.companyLogoLabel')}
          value={companyLogoUrl}
          isSaving={isCompanyLogoSaving}
          onFinish={saveCompanyLogo}
        />
        <TextField
          error={touched.company && Boolean(errors.company)}
          helperText={touched.company && t(errors.company)}
          variant="outlined"
          id="company"
          name="company"
          type="text"
          label={t('components.RealEstateAgentSettingsPage.ProfileForm.company')}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.company}
          className={classes.field}
          InputLabelProps={{
            shrink: false,
          }}
          InputProps={{
            style: {
              height: '40px',
            },
          }}
        />
        <TextField
          error={touched.mls && Boolean(errors.mls)}
          helperText={touched.mls && t(errors.mls)}
          variant="outlined"
          id="mls"
          name="mls"
          type="text"
          label={t('components.RealEstateAgentSettingsPage.ProfileForm.mls')}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.mls}
          className={classes.field}
          InputLabelProps={{
            shrink: false,
          }}
          InputProps={{
            style: {
              height: '40px',
            },
          }}
        />
        <TextField
          error={touched.phoneNumber && Boolean(errors.phoneNumber)}
          helperText={touched.phoneNumber && t(errors.phoneNumber)}
          variant="outlined"
          id="phoneNumber"
          name="phoneNumber"
          type="text"
          label={t('components.RealEstateAgentSettingsPage.ProfileForm.phoneNumber')}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.phoneNumber}
          className={classes.field}
          InputLabelProps={{
            shrink: false,
          }}
          InputProps={{
            inputComponent: PhoneInput,
            style: {
              height: '40px',
            },
          }}
        />
        <TextField
          error={touched.aboutMe && Boolean(errors.aboutMe)}
          helperText={touched.aboutMe && t(errors.aboutMe)}
          variant="outlined"
          id="aboutMe"
          name="aboutMe"
          type="text"
          multiline
          rows="4"
          label={t('components.RealEstateAgentSettingsPage.ProfileForm.aboutMe')}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.aboutMe}
          className={classes.field}
          InputLabelProps={{
            shrink: false,
          }}
        />
        <Typography className={classes.subTitle} variant="body1">
          {t('components.RealEstateAgentSettingsPage.ProfileForm.address')}
        </Typography>
        <AddressSelect form={{ values, setFieldValue }} label={fullAddress} />
        <TextField
          variant="outlined"
          id="streetAddress"
          name="streetAddress"
          type="text"
          label={t('components.RealEstateAgentSettingsPage.ProfileForm.streetAddress')}
          value={streetAddress}
          className={classes.field}
          InputLabelProps={{
            shrink: false,
          }}
          InputProps={{
            style: {
              height: '40px',
            },
          }}
          disabled
        />
        <TextField
          variant="outlined"
          id="city"
          name="city"
          type="text"
          label={t('components.RealEstateAgentSettingsPage.ProfileForm.city')}
          value={city}
          className={classes.field}
          InputLabelProps={{
            shrink: false,
          }}
          InputProps={{
            style: {
              height: '40px',
            },
          }}
          disabled
        />
        <TextField
          variant="outlined"
          id="state"
          name="state"
          type="text"
          label={t('components.RealEstateAgentSettingsPage.ProfileForm.state')}
          value={state}
          className={classes.field}
          InputLabelProps={{
            shrink: false,
          }}
          InputProps={{
            style: {
              height: '40px',
            },
          }}
          disabled
        />
        <TextField
          variant="outlined"
          id="zipCode"
          name="zipCode"
          type="text"
          label={t('components.RealEstateAgentSettingsPage.ProfileForm.zipCode')}
          value={zipCode}
          className={classes.field}
          InputLabelProps={{
            shrink: false,
          }}
          InputProps={{
            style: {
              height: '40px',
            },
          }}
          disabled
        />
        <Button className={classes.button} type="submit" disabled={!isAbleToSubmit}>
          {t('components.RealEstateAgentSettingsPage.ProfileForm.submitButton')}
        </Button>
      </form>
    </div>
  );
};

ProfileForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  currentUser: UserPresenter.shape().isRequired,
  saveProfileImage: PropTypes.func.isRequired,
  isProfileImageSaving: PropTypes.bool.isRequired,
  isCompanyLogoSaving: PropTypes.bool.isRequired,
  saveCompanyLogo: PropTypes.func.isRequired,
};

export default ProfileForm;



// WEBPACK FOOTER //
// ./src/components/RealEstateAgentSettingsPage/components/ProfileForm/ProfileForm.js